import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProfileProviderPF, ProfileProviderPfControllerService, ProfileProviderPFDto } from '../../core/generated';

@Injectable({
  providedIn: 'root'
})
export class ProfileProviderPFCacheListService {
  private profileProviderPFService = inject(ProfileProviderPfControllerService);
  private profileProviderPFListSubject = new BehaviorSubject<ProfileProviderPF[] | null>(null);
  private profileProviderPFCache: { [id: string]: ProfileProviderPF } = {};

  constructor() {
    // Recupera cache do localStorage ao iniciar
    const cache = localStorage.getItem('profileProviderPFCache');
    if (cache) {
      this.profileProviderPFCache = JSON.parse(cache);
    }
  }

  get profileProviderPFList$(): Observable<ProfileProviderPF[] | null> {
    return this.profileProviderPFListSubject.asObservable();
  }

  loadAll(): void {
    this.profileProviderPFService.findAll2(0, 1000, 'id').subscribe({
      next: (response) => {
        const list = response.content as ProfileProviderPF[] | undefined;
        this.profileProviderPFListSubject.next(list || null);
        if (list) {
          // Atualiza cache local
          for (const pf of list) {
            if (pf.id !== undefined) {
              this.profileProviderPFCache[pf.id] = pf;
            }
          }
          localStorage.setItem('profileProviderPFCache', JSON.stringify(this.profileProviderPFCache));
        }
        localStorage.setItem('profileProviderPFList', JSON.stringify(list));
      },
      error: () => {
        const cache = localStorage.getItem('profileProviderPFList');
        if (cache) {
          this.profileProviderPFListSubject.next(JSON.parse(cache));
        }
      }
    });
  }

  getFromCache(): ProfileProviderPF[] | null {
    const cache = localStorage.getItem('profileProviderPFList');
    return cache ? JSON.parse(cache) : null;
  }

  /**
   * Busca um ProfileProviderPF pelo id.
   * Se não estiver no cache, busca no backend, adiciona ao cache e retorna via callback.
   * Sempre use o callback para obter o resultado.
   */
  findProfileProviderPFById(id: number, callback?: (pf: ProfileProviderPF | undefined) => void): ProfileProviderPF | undefined {
    if (!id) {
      if (callback) callback(undefined);
      return undefined;
    }
    // Busca em memória
    if (this.profileProviderPFCache[id]) {
      if (callback) callback(this.profileProviderPFCache[id]);
      return this.profileProviderPFCache[id];
    }
    // Busca no localStorage
    const cache = localStorage.getItem('profileProviderPFCache');
    if (cache) {
      const parsed = JSON.parse(cache);
      if (id) {
        const profilePF = parsed[id] as ProfileProviderPF;
        if (profilePF) {
          this.profileProviderPFCache[id] = profilePF;
          if (callback) callback(profilePF);
          return profilePF;
        }
        if (callback) callback(undefined);
        return undefined;
      }
    }
    // Busca no backend e adiciona ao cache/localStorage (assíncrono)
    this.profileProviderPFService.findById7(id).subscribe({
      next: (profilePF) => {
        if (profilePF) {
          if (profilePF.id !== undefined) {
            this.profileProviderPFCache[profilePF.id] = profilePF;
            localStorage.setItem('profileProviderPFCache', JSON.stringify(this.profileProviderPFCache));
          }
          if (callback) callback(profilePF);
        } else {
          if (callback) callback(undefined);
        }
      },
      error: () => {
        if (callback) callback(undefined);
      }
    });
    return undefined;
  }

  findProfileProviderPFByEmail(email: string): ProfileProviderPF | undefined {
    if (!email) return undefined;
    // Busca em memória
    for (const pf of Object.values(this.profileProviderPFCache)) {
      if (pf.email === email) return pf;
    }
    // Busca no localStorage
    const cache = localStorage.getItem('profileProviderPFCache');
    if (cache) {
      const parsed = JSON.parse(cache);
      for (const pf of Object.values(parsed)) {
        const profilePF = pf as ProfileProviderPF;
        if (profilePF.email === email) {
          if (profilePF.id !== undefined) {
            this.profileProviderPFCache[profilePF.id] = profilePF;
          }
          return profilePF;
        }
      }
    }
    return undefined;
  }

  findProfileProviderPFByCpf(cpf: string, callback?: (pf: ProfileProviderPF | undefined) => void): ProfileProviderPF | undefined {
    if (!cpf) {
      if (callback) callback(undefined);
      return undefined;
    }
    // Busca em memória
    for (const pf of Object.values(this.profileProviderPFCache)) {
      if (pf.cpf === cpf) {
        if (callback) callback(pf);
        return pf;
      }
    }
    // Busca no localStorage
    const cache = localStorage.getItem('profileProviderPFCache');
    if (cache) {
      const parsed = JSON.parse(cache);
      for (const pf of Object.values(parsed)) {
        const profilePF = pf as ProfileProviderPF;
        if (profilePF.cpf === cpf) {
          if (profilePF.id !== undefined) {
            this.profileProviderPFCache[profilePF.id] = profilePF;
          }
          if (callback) callback(profilePF);
          return profilePF;
        }
      }
    }
    // Busca no backend e adiciona ao cache/localStorage (assíncrono)
    this.profileProviderPFService.findByCpf(cpf).subscribe({
      next: (profileDto: ProfileProviderPFDto) => {
        if (profileDto) {
          const profile: ProfileProviderPF = profileDto as any;
          if (profile.id !== undefined) {
            this.profileProviderPFCache[profile.id] = profile;
            localStorage.setItem('profileProviderPFCache', JSON.stringify(this.profileProviderPFCache));
          }
          if (callback) callback(profile);
        } else {
          if (callback) callback(undefined);
        }
      },
      error: () => {
        if (callback) callback(undefined);
      }
    });
    return undefined;
  }

  addProfileProviderPFById(id: number, callback?: (pf: ProfileProviderPF | undefined) => void): void {
    this.findProfileProviderPFById(id, callback);
  }
}
